import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle, 
  Clock, 
  Plus, 
  CreditCard, 
  XCircle, 
  FileText 
} from "lucide-react";
import { formatCurrency, type Currency } from "@/lib/currency";
import { formatDistanceToNow } from "date-fns";

interface Participant {
  id: string;
  email: string;
  has_paid: boolean;
}

interface Request {
  id: string;
  product_name: string;
  total_price: number;
  status: string;
  created_at: string;
  deadline: string;
  participants: Participant[];
}

interface ActivityLogProps {
  requests: Request[];
  currency: Currency;
}

interface ActivityItem {
  id: string;
  type: 'created' | 'completed' | 'expired' | 'payment';
  title: string;
  description: string;
  timestamp: Date;
  amount?: number;
}

export function ActivityLog({ requests, currency }: ActivityLogProps) {
  const activities = useMemo(() => {
    const items: ActivityItem[] = [];

    requests.forEach(r => {
      // Collection created
      items.push({
        id: `${r.id}-created`,
        type: 'created',
        title: `Collection created`,
        description: r.product_name,
        timestamp: new Date(r.created_at),
        amount: r.total_price,
      });

      // Collection completed
      if (r.status === 'completed') {
        items.push({
          id: `${r.id}-completed`,
          type: 'completed',
          title: `Collection completed`,
          description: `${r.product_name} — all payments received`,
          timestamp: new Date(r.deadline),
          amount: r.total_price,
        });
      }

      // Collection expired
      if (r.status === 'expired' || (r.status === 'pending' && new Date(r.deadline) < new Date())) {
        items.push({
          id: `${r.id}-expired`,
          type: 'expired',
          title: `Collection expired`,
          description: `${r.product_name} — deadline passed`,
          timestamp: new Date(r.deadline),
        });
      }

      // Individual payments
      r.participants.filter(p => p.has_paid).forEach(p => {
        items.push({
          id: `${r.id}-${p.id}-paid`,
          type: 'payment',
          title: `Payment received`,
          description: `${p.email} paid for ${r.product_name}`,
          timestamp: new Date(r.created_at), // approximate
          amount: r.total_price / r.participants.length,
        });
      });
    });

    return items.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, 50);
  }, [requests]);

  const getIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'created': return <Plus className="h-4 w-4 text-cyan-600" />;
      case 'completed': return <CheckCircle className="h-4 w-4 text-emerald-600" />;
      case 'expired': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'payment': return <CreditCard className="h-4 w-4 text-cyan-600" />;
    }
  };

  const getBg = (type: ActivityItem['type']) => {
    switch (type) {
      case 'created': return 'bg-cyan-500/10';
      case 'completed': return 'bg-emerald-500/10';
      case 'expired': return 'bg-red-500/10';
      case 'payment': return 'bg-cyan-500/10';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Activity Log</h2>
        <p className="text-sm text-muted-foreground">Recent actions and payment events</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {activities.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No activity yet. Create your first collection to see events here.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {activities.map((activity, i) => (
                <div key={activity.id} className="flex items-start gap-3 py-3 border-b last:border-0">
                  <div className={`p-2 rounded-lg mt-0.5 ${getBg(activity.type)}`}>
                    {getIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{activity.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{activity.description}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {activity.amount && (
                      <p className="text-sm font-semibold">{formatCurrency(activity.amount, currency)}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
